import { childAgeMonths } from "@/lib/childAge";
import { Badge } from '@/components/ui/badge';
import { User, Briefcase, Heart, Users, Clock, MapPin, Award, ShieldCheck, Calendar, Languages, Baby } from 'lucide-react';
import { formatChildrenSummary } from '@/lib/playDateUtils';

interface NapTime {
  start: string;
  end: string;
}

interface AboutMeDisplayProps {
  role?: string;
  parentType?: string | null;
  workStatus?: string | null;
  breastfeeding?: boolean | null;
  singleParent?: boolean | null;
  napTimes?: NapTime[] | null;
  district?: string | null;
  languages?: string[] | null;
  experienceLevel?: string | null;
  qualifications?: string[] | null;
  availabilityType?: string | null;
  childrenInfo?: Array<{ age_months?: number; birth_ym?: string }> | null;
}

const WORK_STATUS_LABELS: Record<string, string> = {
  full_time_parent: 'Full-time parent',
  part_time_parent: 'Working part-time',
  working_full_time: 'Working full time',
};

const isNannyRole = (role?: string) => role === 'nanny' || role === 'childminder';

export function AboutMeDisplay({ role, parentType, workStatus, breastfeeding, singleParent, napTimes, district, languages, experienceLevel, qualifications, availabilityType, childrenInfo }: AboutMeDisplayProps) {
  const isNanny = isNannyRole(role);
  
  const hasParentInfo = parentType || workStatus || breastfeeding || singleParent;
  const hasNannyInfo = experienceLevel || (qualifications && qualifications.length > 0) || availabilityType;
  const hasChildren = childrenInfo && childrenInfo.length > 0;
  const hasSharedInfo = district || (napTimes && napTimes.length > 0) || hasChildren;
  
  const hasAnyInfo = (isNanny ? hasNannyInfo : hasParentInfo) || hasSharedInfo;
  
  if (!hasAnyInfo) return null;

  return (
    <div>
      <h3 className="font-semibold mb-2">About Me</h3>
      <div className="flex flex-wrap gap-2">
        {/* Parent badges */}
        {!isNanny && (
          <>
            {parentType && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {parentType === 'mum' ? 'Mum' : 'Dad'}
              </Badge>
            )}
            {workStatus && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                {WORK_STATUS_LABELS[workStatus] || workStatus}
              </Badge>
            )}
            {breastfeeding && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                Breastfeeding
              </Badge>
            )}
            {singleParent && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                Single parent
              </Badge>
            )}
          </>
        )}

        {/* Nanny badges */}
        {isNanny && (
          <>
            {experienceLevel && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Award className="h-3 w-3" />
                {experienceLevel}
              </Badge>
            )}
            {qualifications && qualifications.map((qual) => (
              <Badge key={qual} variant="secondary" className="flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" />
                {qual}
              </Badge>
            ))}
            {availabilityType && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {availabilityType}
              </Badge>
            )}
          </>
        )}

        {/* Shared badges */}
        {district && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {district.charAt(0).toUpperCase() + district.slice(1)}
          </Badge>
        )}
        {hasChildren && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Baby className="h-3 w-3" />
            {formatChildrenSummary(childrenInfo!.map(c => childAgeMonths(c)))}
          </Badge>
        )}
        {napTimes && napTimes.length > 0 && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Nap{napTimes.length > 1 ? 's' : ''}: {napTimes.map(nt => `${nt.start}–${nt.end}`).join(', ')}
          </Badge>
        )}
      </div>
    </div>
  );
}
